import React from 'react';
import { Linking } from 'react-native';
import { Box, Divider, Heading, Link, ScrollView, Text, VStack } from 'native-base';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <VStack space={2}>
        <Heading size="md">{title}</Heading>
        {children}
    </VStack>
);

export default function PrivacyPolicyPage() {
    const openDeletionPage = () => Linking.openURL('https://grams.inweon.com/account-deletion');
    const emailPrivacy = () => Linking.openURL('mailto:grainiqinfo@gmail.com?subject=InstaGrainX%20Privacy%20Request');

    return (
        <Box flex={1} bg="white" _dark={{ bg: 'gray.900' }}>
            <ScrollView>
                <VStack width="100%" maxWidth="840px" alignSelf="center" space={6} px={6} py={10}>
                    <VStack space={2}>
                        <Heading size="xl">InstaGrainX Privacy Policy</Heading>
                        <Text color="gray.600" _dark={{ color: 'gray.300' }}>
                            Effective and last updated: August 2, 2026
                        </Text>
                    </VStack>

                    <Text>
                        GrainIQ IT Innovations Pvt Ltd ("GrainIQ", "we", "us", or "our") provides
                        InstaGrainX, a grain-quality analysis platform. This policy explains how the
                        InstaGrainX Android app and related services collect, use, disclose, retain,
                        and protect information.
                    </Text>

                    <Divider />

                    <Section title="Information we collect">
                        <Text>
                            Account and organisation information, such as your name, username, email
                            address, phone number, organisation, role, and subscription details.
                        </Text>
                        <Text>
                            Grain-analysis content, including sample images you choose or capture,
                            sample identifiers, grain profiles, measurement inputs, analysis results,
                            reports, notes, and related files.
                        </Text>
                        <Text>
                            Device and technical information, such as device and app identifiers,
                            operating-system and app versions, network status, nearby Bluetooth device
                            information, diagnostic data, crash logs, and app performance and usage data.
                        </Text>
                        <Text>
                            Permission-related information. On supported Android versions, InstaGrainX
                            may request photo or storage access to select and save sample images and
                            reports, nearby-device or Bluetooth access to connect to GrainIQ hardware,
                            and location permission where Android requires it for Bluetooth discovery.
                            InstaGrainX does not use location information for advertising.
                        </Text>
                    </Section>

                    <Section title="How we use information">
                        <Text>
                            We use information to authenticate users; provide grain analysis, reporting,
                            synchronisation, device connectivity, and subscription features; maintain
                            security and prevent abuse; diagnose failures and improve reliability; offer
                            support; and comply with applicable legal obligations.
                        </Text>
                    </Section>

                    <Section title="Service providers and disclosures">
                        <Text>
                            We use service providers for cloud hosting and storage, authentication,
                            notifications, analytics, crash reporting, and technical support. These may
                            include Amazon Web Services, Google Firebase, Microsoft App Center, and
                            Bugsnag. They process information for us under their applicable contractual
                            and security obligations. We may also disclose information when required by
                            law, to protect rights or safety, or as part of a corporate transaction. We do
                            not sell personal information or use it for third-party behavioural advertising.
                        </Text>
                    </Section>

                    <Section title="Retention, security, and international processing">
                        <Text>
                            We retain information while your account is active and as needed to provide
                            the service, meet legal or contractual duties, resolve disputes, and protect
                            the platform. Retention periods vary by data type and business requirement.
                            We use administrative, technical, and organisational safeguards designed to
                            protect information. Information may be processed in countries other than
                            your own, subject to applicable legal safeguards.
                        </Text>
                    </Section>

                    <Section title="Your choices and account deletion">
                        <Text>
                            You can limit optional permissions in Android settings, although some app
                            features may then be unavailable. To request deletion of your InstaGrainX
                            account and associated personal data, use the in-app deletion request in
                            Settings or follow the instructions on our account-deletion page.
                        </Text>
                        <Link onPress={openDeletionPage}>Account-deletion instructions</Link>
                    </Section>

                    <Section title="Children">
                        <Text>
                            InstaGrainX is a business service and is not directed to children under 13,
                            or the minimum age required by local law. We do not knowingly collect personal
                            information from children through the service.
                        </Text>
                    </Section>

                    <Section title="Changes to this policy">
                        <Text>
                            We may update this policy to reflect service, legal, or operational changes.
                            We will post the revised policy here and update its effective date.
                        </Text>
                    </Section>

                    <Section title="Contact us">
                        <Text>
                            GrainIQ IT Innovations Pvt Ltd{`\n`}
                            Plot 4, 2nd Floor, Minarch Tower, Sector 44{`\n`}
                            Gurugram, Haryana 122003, India
                        </Text>
                        <Link onPress={emailPrivacy}>grainiqinfo@gmail.com</Link>
                    </Section>
                </VStack>
            </ScrollView>
        </Box>
    );
}
